"use client"

import * as React from "react"

const MarkAddedContext = React.createContext<(() => void) | null>(null)

/**
 * Called by {@link AddToCartForm} after a successful add. No-op outside a
 * {@link ResetAfterAdd} boundary.
 */
export function useMarkAdded(): () => void {
  return React.useContext(MarkAddedContext) ?? noop
}

function noop() {}

/**
 * Remounts `children` when the route is hidden AFTER a successful add to cart.
 * With `cacheComponents`, Next.js keeps visited routes mounted inside React
 * `<Activity>` instead of unmounting them, so an item's customizer kept the
 * diner's picks (options, quantity, notes) when the item was opened again.
 * That's wanted for an abandoned customization (the draft survives), but
 * after an add the next open must start from the item's defaults. Hiding runs
 * layout-effect cleanups, so the key is bumped there — never while visible,
 * which would flash the defaults during the modal's close animation.
 */
export function ResetAfterAdd({ children }: { children: React.ReactNode }) {
  const [instance, setInstance] = React.useState(0)
  const added = React.useRef(false)
  const markAdded = React.useCallback(() => {
    added.current = true
  }, [])

  React.useLayoutEffect(() => {
    return () => {
      if (!added.current) return
      added.current = false
      setInstance((n) => n + 1)
    }
  }, [])

  return (
    <MarkAddedContext.Provider value={markAdded}>
      <React.Fragment key={instance}>{children}</React.Fragment>
    </MarkAddedContext.Provider>
  )
}
